import { useTranslation } from "react-i18next";
import styles from "./Login.module.css";
import React from "react";
import { RegisterRequest } from "../../api/models";
import { Password } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Grid, Alert, TextField, InputAdornment, Button } from "@mui/material";
import EmailIcon from '@mui/icons-material/Email';
import SaveIcon from '@mui/icons-material/Save';
import { getApiError, getService } from "../../app/api";
import { AuthService, UserService } from "../../api/services";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";

interface RegisterModel extends RegisterRequest {
  password_check?: string;
}

export interface RegisterProps {
  onRegister?: () => void;
}

export default function RegisterView(props: RegisterProps) {
  const { t, i18n } = useTranslation();
  let [loading, setLoading] = React.useState(false);
  let [item, setItem] = React.useState<RegisterModel>({} as RegisterModel);
  let [error, setError] = React.useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  
  function setValue(field: keyof RegisterModel, e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setItem(prev => {
      return {
        ...prev,
        [field]: e.target.value
      } as RegisterModel
    });
  }

  function handleRegister() {
    // Validation: Check if email is set
    if (!item.email) {
      setError(t("Email is required"));
      return;
    }

    if (!item.password) {
      setError(t("Password is required"));
      return;
    }

    // Validation: Check if passwords match
    if (item.password !== item.password_check) {
      setError(t("Passwords do not match"));
      return;
    }

    setError(null);
    setLoading(true);
    getService(UserService).register(item)
      .then(() => {
        enqueueSnackbar(t("Registration successful!"), { variant: "success" });
        props.onRegister?.();
      })
      .catch(err => {
        setError(getApiError(err));
        enqueueSnackbar(t("Registration failed: {{error}}", { error: getApiError(err) }), { variant: "error" });
      })
      .finally(() => {
        setLoading(false);
      });
  }

  return (
    <Grid container spacing={1} className={styles.root} >
      {error && (
        <Grid item xs={12}>
          <Alert severity="error">{error}</Alert>
        </Grid>)
      }
      <Grid item xs={12}>
        <TextField required
          className={styles.textfield}
          onChange={e => setValue('email', e)}
          value={item.email}
          variant="filled"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailIcon />
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField required
          type="password"
          className={styles.textfield}
          onChange={e => setValue('password', e)}
          value={item.password}
          variant="filled"
          placeholder={t("Password")}
          onKeyDown={e => e.key == 'Enter' && handleRegister()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Password />
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField required
          type="password"
          className={styles.textfield}
          onChange={e => setValue('password_check', e)}
          value={item.password_check}
          placeholder={t("Confirm Password")}
          variant="filled"
          onKeyDown={e => e.key == 'Enter' && handleRegister()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Password />
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item xs={12} style={{ marginTop: '1rem' }}>
        <LoadingButton
          variant="outlined"
          loading={loading}
          loadingPosition="start"
          startIcon={<SaveIcon />}
          onClick={handleRegister}>
          {t('register')}
        </LoadingButton>
      </Grid>
      <Grid item xs={12}>
            <Button style={{ marginTop: '1rm' }}                        
                variant="outlined"
                onClick={props.onRegister} >
                {t('Login')}
            </Button>
        </Grid>
    </Grid>
  )
}